import useAws from "@/aws/useAws";
import Luxon from "@/libs/luxon";
import { useChatsStore } from "@/store/useChatsStore";
import { useDownloadAttachmentsStore } from "@/store/useDownloadAttachmentsStore";
import { downloadBlob, getFullPath } from "@/utils/assets";
import { ref } from "@vue/composition-api";
import { saveAs } from "file-saver";
import JSZip from "jszip";

/**
 * @note if we like to have multiple different archivers in the future, we may can use some kind of factory.
 */

const isGeneratingArchive = ref(false);

export default function useZipFileArchiver() {
    const generateArchive = async (): Promise<void> => {
        const { attachments } = useDownloadAttachmentsStore();
        if (attachments.length === 0) {
            throw new Error("No attachments to archive.");
        }

        isGeneratingArchive.value = true;

        try {
            const zip = new JSZip();

            const fullFilePaths = attachments.map((asset) => getFullPath(asset, true));
            const assets: Array<{ fileName: string; signedUrl: string }> = (
                await useAws.getSignedUrls(fullFilePaths)
            ).map(({ signed }, index) => ({
                fileName: attachments[index].fileName,
                signedUrl: signed,
            }));

            await Promise.all(
                assets.map((asset) =>
                    downloadBlob(asset.signedUrl).then<JSZip>(({ data }: { data: Blob }) =>
                        zip.file(asset.fileName, data)
                    )
                )
            );

            zip.generateAsync({ type: "blob" }).then((blob: Blob) => {
                const { activeChat } = useChatsStore();

                saveAs(
                    blob,
                    `attachments_${activeChat.contact?.name ?? activeChat.chat.identity}_${Luxon.DateTime.now().toISO({
                        format: "basic",
                        includeOffset: false,
                        suppressMilliseconds: true,
                    })}.zip`
                );
            });
        } catch (e) {
            isGeneratingArchive.value = false;
            throw e;
        }

        isGeneratingArchive.value = false;
    };

    return {
        generateArchive,
        isGeneratingArchive,
    };
}
